import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { news } from "@/mocks/news";
import NewsCard from "@/components/feature/NewsCard";
import SectionHeading from "@/components/base/SectionHeading";
import { shareContent } from "@/utils/format";
import usePageMeta from "@/hooks/usePageMeta";

export default function NewsDetail() {
  const { newsId } = useParams();
  const [toast, setToast] = useState("");
  const item = news.find((n) => n.id === newsId);

  usePageMeta(
    item ? `${item.title} - 51每日大赛` : "新闻内容不可用 - 51每日大赛",
    item ? item.excerpt : "该新闻内容不可用，请返回新闻列表浏览其他资讯。",
    item ? `${item.title},${item.category},体育新闻,51每日大赛` : "新闻资讯,51每日大赛",
  );

  const handleShare = async () => {
    if (!item) return;
    const res = await shareContent(item.title, item.excerpt, window.location.href);
    if (res === "copied") setToast("链接已复制到剪贴板");
    else if (res === "shared") setToast("感谢分享！");
    else setToast("分享失败，请手动复制链接");
    setTimeout(() => setToast(""), 2200);
  };

  if (!item) {
    return (
      <div className="w-full px-4 md:px-8 py-24 text-center">
        <i className="ri-article-line text-5xl text-foreground-300" />
        <h1 className="mt-4 font-heading text-xl text-foreground-900">新闻内容不可用</h1>
        <p className="mt-2 text-sm text-foreground-600">您访问的新闻可能已被移除或链接有误。</p>
        <Link
          to="/news"
          className="inline-flex items-center gap-2 mt-6 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          返回新闻列表
        </Link>
      </div>
    );
  }

  const related = news.filter((n) => n.id !== item.id && n.category === item.category).slice(0, 2);
  const fallbackRelated = news.filter((n) => n.id !== item.id).slice(0, 3);
  const recommendations = related.length > 0 ? related : fallbackRelated;

  return (
    <div className="w-full">
      <div className="w-full bg-background-50 border-b border-background-200">
        <div className="w-full px-4 md:px-8 py-6">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/news" className="hover:text-primary-600 transition-colors">新闻资讯</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800 truncate max-w-[220px]">{item.title}</span>
          </nav>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        <div className="max-w-3xl mx-auto">
          <span className="inline-block px-2.5 py-0.5 rounded-md bg-accent-100 text-accent-900 text-xs font-label mb-3">
            {item.category}
          </span>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 leading-snug">
            {item.title}
          </h1>
          <div className="mt-4 flex items-center gap-4 text-xs text-foreground-500 flex-wrap">
            <span className="flex items-center gap-1.5"><i className="ri-user-line" /> {item.author}</span>
            <span className="flex items-center gap-1.5"><i className="ri-calendar-line" /> {item.date}</span>
          </div>

          <div className="mt-6 rounded-lg overflow-hidden border border-background-200 bg-background-50">
            <div className="w-full h-56 md:h-80 overflow-hidden bg-background-100">
              <img src={item.cover} alt={item.title} className="w-full h-full object-cover object-top" />
            </div>
          </div>

          <article className="mt-8 space-y-5">
            {item.content.map((p, i) => (
              <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed">
                {p}
              </p>
            ))}
          </article>

          <div className="mt-8 flex items-center gap-2 flex-wrap">
            {item.tags.map((t) => (
              <span key={t} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs">{t}</span>
            ))}
          </div>

          <div className="mt-8 pt-6 border-t border-background-200 flex items-center justify-between gap-4 flex-wrap">
            <span className="text-sm text-foreground-600">觉得有用？分享给更多球迷</span>
            <button
              type="button"
              onClick={handleShare}
              className="inline-flex items-center justify-center gap-2 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-share-forward-line" /> 分享文章
            </button>
          </div>
        </div>

        <div className="mt-14 max-w-5xl mx-auto">
          <SectionHeading eyebrow="Read More" title="相关文章推荐" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {recommendations.map((n) => (
              <NewsCard key={n.id} item={n} />
            ))}
          </div>
        </div>
      </div>

      {toast ? (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-lg bg-secondary-900 text-background-50 text-sm animate-fade-up">
          {toast}
        </div>
      ) : null}
    </div>
  );
}